'use client';

import { useState } from 'react';

import Link from 'next/link';

import { ArrowRightIcon, CheckCircle2, XCircle } from 'lucide-react';

export function RoiCalculator() {
  const [leadsPerMonth, setLeadsPerMonth] = useState(100);
  const [vertical, setVertical] = useState<
    'gig' | 'healthcare' | 'saas' | 'finserv'
  >('saas');

  const verticalConfig = {
    gig: { name: 'Gig Economy', cpl: 30, agencyRetainer: 8000, sdrCost: 10000 },
    healthcare: {
      name: 'Healthcare',
      cpl: 50,
      agencyRetainer: 15000,
      sdrCost: 12000,
    },
    saas: {
      name: 'SaaS & Technology',
      cpl: 75,
      agencyRetainer: 12000,
      sdrCost: 11000,
    },
    finserv: {
      name: 'Financial Services',
      cpl: 100,
      agencyRetainer: 20000,
      sdrCost: 14000,
    },
  };

  const config = verticalConfig[vertical];
  const productCost = leadsPerMonth * config.cpl;
  const agencyCost = config.agencyRetainer;
  const sdrCost = config.sdrCost + 2000; // SDR salary + tools
  const maxCost = Math.max(productCost, agencyCost, sdrCost);

  const savingsVsAgency = agencyCost - productCost;
  const savingsVsSdr = sdrCost - productCost;
  const savingsPercentAgency = Math.round((savingsVsAgency / agencyCost) * 100);
  const savingsPercentSdr = Math.round((savingsVsSdr / sdrCost) * 100);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Controls */}
      <div className="bg-card rounded-xl border p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Vertical selector */}
          <div>
            <label className="text-sm font-medium">Your vertical</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(
                Object.keys(verticalConfig) as Array<
                  keyof typeof verticalConfig
                >
              ).map((key) => (
                <button
                  key={key}
                  onClick={() => setVertical(key)}
                  className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    vertical === key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  {verticalConfig[key].name}
                </button>
              ))}
            </div>
          </div>

          {/* Leads per month slider */}
          <div>
            <label className="text-sm font-medium">
              Leads per month:{' '}
              <span className="text-primary">{leadsPerMonth}</span>
            </label>
            <input
              type="range"
              min={10}
              max={500}
              step={10}
              value={leadsPerMonth}
              onChange={(e) => setLeadsPerMonth(Number(e.target.value))}
              className="accent-primary mt-4 w-full"
            />
            <div className="text-muted-foreground mt-1 flex justify-between text-xs">
              <span>10</span>
              <span>250</span>
              <span>500</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cost comparison */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <CostCard
          title="In-House SDR"
          cost={sdrCost}
          maxCost={maxCost}
          details={[
            {
              label: 'SDR salary',
              value: `$${config.sdrCost.toLocaleString()}/mo`,
            },
            { label: 'Tools & overhead', value: '$2,000/mo' },
            { label: 'Ramp-up time', value: '2-3 months' },
          ]}
          cons={[
            'Fixed cost regardless of output',
            'Hiring and training overhead',
            '2-3 month ramp before results',
          ]}
        />

        <CostCard
          title="Traditional Agency"
          cost={agencyCost}
          maxCost={maxCost}
          details={[
            {
              label: 'Monthly retainer',
              value: `$${config.agencyRetainer.toLocaleString()}/mo`,
            },
            { label: 'Contract', value: '6-12 months' },
            { label: 'Results timeline', value: '4-8 weeks' },
          ]}
          cons={[
            'Locked into long contracts',
            'Pay regardless of lead quality',
            'Opaque lead qualification',
          ]}
        />

        <CostCard
          title="Selenos"
          cost={productCost}
          maxCost={maxCost}
          highlighted
          details={[
            { label: 'Cost per lead', value: `$${config.cpl}` },
            {
              label: `${leadsPerMonth} leads/mo`,
              value: `$${productCost.toLocaleString()}/mo`,
            },
            { label: 'Contract', value: 'None' },
          ]}
          pros={[
            'Pay only for qualified leads',
            'No contracts or lock-in',
            'Results in days, not weeks',
          ]}
        />
      </div>

      {/* Savings summary */}
      {savingsVsAgency > 0 && (
        <div className="bg-primary/5 border-primary/20 rounded-xl border p-6 text-center">
          <p className="text-muted-foreground text-sm">
            With {leadsPerMonth} leads/month in {config.name}, you save:
          </p>
          <div className="mt-3 flex flex-col items-center gap-4 md:flex-row md:justify-center md:gap-12">
            <div>
              <p className="text-primary text-3xl font-semibold tracking-tight">
                ${savingsVsAgency.toLocaleString()}/mo
              </p>
              <p className="text-muted-foreground text-sm">
                vs. agency ({savingsPercentAgency}% savings)
              </p>
            </div>
            {savingsVsSdr > 0 && (
              <div>
                <p className="text-primary text-3xl font-semibold tracking-tight">
                  ${savingsVsSdr.toLocaleString()}/mo
                </p>
                <p className="text-muted-foreground text-sm">
                  vs. in-house SDR ({savingsPercentSdr}% savings)
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="text-center">
        <p className="text-muted-foreground text-sm">
          Want a custom analysis for your specific ICP and volume?
        </p>
        <Link
          href="/contact"
          className="bg-primary text-primary-foreground mt-4 inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium transition-colors hover:opacity-90"
        >
          Get Your Custom ROI Analysis
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>

      {/* Disclaimer */}
      <p className="text-muted-foreground/70 text-center text-xs">
        Cost per lead, agency retainer, and in-house SDR figures are sample
        industry benchmarks for illustration. Actual costs vary by vertical,
        region, volume, and campaign. Request a custom analysis for figures
        specific to your business.
      </p>
    </div>
  );
}

function CostCard({
  title,
  cost,
  maxCost,
  highlighted = false,
  details,
  pros,
  cons,
}: {
  title: string;
  cost: number;
  maxCost: number;
  highlighted?: boolean;
  details: { label: string; value: string }[];
  pros?: string[];
  cons?: string[];
}) {
  const barHeight = Math.max(20, (cost / maxCost) * 100);

  return (
    <div
      className={`flex flex-col rounded-xl border p-5 ${
        highlighted ? 'border-primary bg-primary/5' : 'bg-card'
      }`}
    >
      <p className="text-sm font-medium">{title}</p>
      <p
        className={`mt-1 text-2xl font-semibold tracking-tight ${highlighted ? 'text-primary' : ''}`}
      >
        ${cost.toLocaleString()}
        <span className="text-muted-foreground text-sm font-normal">/mo</span>
      </p>

      {/* Cost bar */}
      <div className="bg-muted my-4 h-2 w-full overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            highlighted ? 'bg-primary' : 'bg-muted-foreground/30'
          }`}
          style={{ width: `${barHeight}%` }}
        />
      </div>

      {/* Details */}
      <div className="space-y-2">
        {details.map((d) => (
          <div key={d.label} className="flex justify-between text-xs">
            <span className="text-muted-foreground">{d.label}</span>
            <span className="font-medium">{d.value}</span>
          </div>
        ))}
      </div>

      {/* Pros/Cons */}
      <div className="mt-4 space-y-1.5">
        {pros?.map((p) => (
          <div key={p} className="flex items-start gap-1.5">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
            <span className="text-xs">{p}</span>
          </div>
        ))}
        {cons?.map((c) => (
          <div key={c} className="flex items-start gap-1.5">
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
            <span className="text-muted-foreground text-xs">{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
